import React, { useState } from 'react';
import { Card, Button, Icon } from '../../common';
import { WORKFLOW_STEPS } from '../../../constants';
import type { WhatsAppBusinessData } from '../../../types/whatsapp';

export interface MessageSenderProps {
  whatsappBusinessData: WhatsAppBusinessData;
  onSendMessage: (phoneNumber: string, message: string) => void;
}

export const MessageSender: React.FC<MessageSenderProps> = ({
  whatsappBusinessData,
  onSendMessage
}) => {
  const [testPhoneNumber, setTestPhoneNumber] = useState('+5491133261336');
  const [testMessage, setTestMessage] = useState('Hola! Este es un mensaje de prueba desde WhatsApp Business API.');

  const getWorkflowStepStatus = (step: number) => {
    if (whatsappBusinessData.workflow_step! > step) {
      return 'completed';
    } else if (whatsappBusinessData.workflow_step === step) {
      return 'current';
    } else {
      return 'pending';
    }
  };

  const getWorkflowStepIcon = (step: number) => {
    const status = getWorkflowStepStatus(step);
    if (status === 'completed') {
      return <Icon name="check" className="w-5 h-5 text-green-600" />;
    } else if (status === 'current') {
      return <Icon name="loader" className="w-5 h-5 text-blue-600 animate-spin" />;
    } else {
      return <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />;
    }
  };

  const handleSendMessage = () => {
    if (!testPhoneNumber.trim()) {
      alert('Por favor, ingresa un número de teléfono para la prueba');
      return;
    }
    onSendMessage(testPhoneNumber, testMessage);
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-[#424242] flex items-center gap-2">
          <Icon name="settings" className="w-5 h-5 text-[#424242]" />
          WhatsApp Business API - Envío de Mensajes
        </h3>
      </div>

      {/* Data Available Badge */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-2 text-blue-800">
          <Icon name="check" className="w-5 h-5" />
          <span className="font-medium">Datos Cargados desde localStorage</span>
        </div>
        <p className="text-blue-700 text-sm mt-1">
          WABA ID: {whatsappBusinessData.waba_id} | Business ID: {whatsappBusinessData.business_id}
        </p>
      </div>

      {/* Workflow Steps */}
      <div className="mt-4">
        <h4 className="text-base font-semibold text-[#424242] mb-4 flex items-center gap-2">
          <Icon name="zap" className="w-5 h-5 text-[#424242]" />
          Envío de Mensajes WhatsApp
        </h4>
        
        <div className="space-y-4 mb-6">
          <div className="flex items-center gap-3">
            {getWorkflowStepIcon(1)}
            <span className={`text-sm font-medium ${getWorkflowStepStatus(1) === 'completed' ? 'text-green-600' : 'text-gray-600'}`}>
              1. Intercambiar código por token de acceso (setup inicial)
            </span>
          </div>
          <div className="flex items-center gap-3">
            {getWorkflowStepIcon(2)}
            <span className={`text-sm font-medium ${getWorkflowStepStatus(2) === 'completed' ? 'text-green-600' : 'text-gray-600'}`}>
              2. Suscribirse a webhooks (setup inicial)
            </span>
          </div>
          <div className="flex items-center gap-3">
            {getWorkflowStepIcon(3)}
            <span className={`text-sm font-medium ${getWorkflowStepStatus(3) === 'completed' ? 'text-green-600' : 'text-gray-600'}`}>
              3. Obtener números de teléfono
            </span>
          </div>
          <div className="flex items-center gap-3">
            {getWorkflowStepIcon(4)}
            <span className={`text-sm font-medium ${getWorkflowStepStatus(4) === 'completed' ? 'text-green-600' : 'text-gray-600'}`}>
              4. Enviar mensaje
            </span>
          </div>
        </div>

        {/* Message Configuration */}
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <h5 className="font-medium text-gray-800 mb-3">Configuración del Mensaje</h5>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número de teléfono destino (con código de país)
              </label>
              <input
                type="text"
                value={testPhoneNumber}
                onChange={(e) => setTestPhoneNumber(e.target.value)}
                placeholder="Ej: +5491123456789"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mensaje
              </label>
              <textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Workflow Error */}
        {whatsappBusinessData.workflow_error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2">
              <Icon name="x" className="w-5 h-5 text-red-600" />
              <span className="font-medium text-red-800">Error al enviar mensaje:</span>
            </div>
            <p className="text-red-700 text-sm mt-1">{whatsappBusinessData.workflow_error}</p>
          </div>
        )}

        {/* Send Button */}
        <div className="flex justify-center">
          <Button
            variant="primary"
            icon={whatsappBusinessData.workflow_loading ? "loader" : "send"}
            loading={whatsappBusinessData.workflow_loading}
            onClick={handleSendMessage}
            className="w-full max-w-[280px]"
          >
            {whatsappBusinessData.workflow_loading ? 'Enviando Mensaje...' : 'Enviar Mensaje'}
          </Button>
        </div>

        {/* Phone Numbers Results */}
        {whatsappBusinessData.phone_numbers && whatsappBusinessData.phone_numbers.length > 0 && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h5 className="font-medium text-green-800 mb-2">Números de Teléfono Disponibles:</h5>
            {whatsappBusinessData.phone_numbers.map((phone, index) => (
              <div key={index} className="text-sm text-green-700 font-mono">
                {phone.display_phone_number} (ID: {phone.id})
              </div>
            ))}
          </div>
        )}

        {/* Message Result */}
        {whatsappBusinessData.test_message_result && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h5 className="font-medium text-green-800 mb-2">Mensaje Enviado:</h5>
            <pre className="text-sm text-green-700 font-mono">
              {JSON.stringify(whatsappBusinessData.test_message_result, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </Card>
  );
};